import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';
import Input from './ui/Input';
import Button from './ui/Button';

const CambiaPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  
  const [form, setForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [show, setShow] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const toggleShow = (field) => setShow(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });

    if (form.newPassword !== form.confirmPassword) {
      return setStatus({ type: 'error', message: t('change_password.errors.mismatch') || 'Le password non corrispondono' });
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: form.oldPassword
      });

      if (authError) {
        throw new Error(t('change_password.errors.wrong_old') || 'Password attuale errata');
      }

      const { error: updateError } = await supabase.auth.updateUser({ 
        password: form.newPassword 
      });

      if (updateError) throw updateError;

      setStatus({ type: 'success', message: t('change_password.success') || 'Password aggiornata con successo!' });
      setTimeout(() => navigate(-1), 2000);

    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-card rounded-3xl border border-border shadow-elevation-1 overflow-hidden max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="flex items-center gap-4 mb-4"></div>
        <h2 className="text-2xl font-black text-foreground uppercase tracking-tighter">
          {t('change_password.title') || 'Cambia Password'}
        </h2>
        <p className="text-sm text-muted-foreground font-medium">
          {t('change_password.subtitle') || 'Aggiorna le tue credenziali di accesso'}
        </p>
      </div>
      
      <div className="p-8">
        {status.message && (
          <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 border ${
            status.type === 'success' 
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600' 
              : 'bg-red-500/10 border-red-500/20 text-red-600'
          }`}>
            <Icon name={status.type === 'success' ? "CheckCircle" : "AlertCircle"} size={20} />
            <span className="text-[11px] font-black uppercase tracking-widest">{status.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-primary/70">
              {t('change_password.security_section') || 'Sicurezza Account'}
            </h3>
            
            {/* Stara lozinka */}
            <div className="relative">
              <Input 
                type={show.oldPassword ? 'text' : 'password'}
                name="oldPassword"
                label={t('change_password.labels.old') || 'Password Attuale'}
                value={form.oldPassword}
                onChange={handleChange}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => toggleShow('oldPassword')}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={show.oldPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            <div className="h-px bg-border w-full my-2" />

            {/* Nova + potvrda */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div className="relative">
                <Input 
                  type={show.newPassword ? 'text' : 'password'}
                  name="newPassword"
                  label={t('change_password.labels.new') || 'Nuova Password'}
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => toggleShow('newPassword')}
                  className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Icon name={show.newPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              <div className="relative">
                <Input 
                  type={show.confirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  label={t('change_password.labels.confirm') || 'Conferma Password'}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => toggleShow('confirmPassword')}
                  className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Icon name={show.confirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

            </div>
          </div>

          <div className="pt-6">
            <Button 
              type="submit" 
              fullWidth 
              loading={loading} 
              size="lg"
              className="shadow-lg shadow-primary/20"
            >
              {t('change_password.submit_btn') || 'Salva Modifiche'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CambiaPassword;